import React, { useState } from 'react';
import { UserIcon, BriefcaseIcon, TagIcon, HeartHandshakeIcon } from 'lucide-react';
import { ConnectionPageLayout } from '../../components/connections/ConnectionPageLayout';
import { FilterSection } from '../../components/UI/FilterSidebar';
import { OpportunityItem } from '../../components/connections/OpportunityPreviewCard';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
const ConnectWithSupport = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const categories = ['Marketing', 'Social Media', 'Author Assistant', 'Research', 'Administrative', 'Event Planning', 'Email Management', 'Website Management'];
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    serviceType: '',
    projectDescription: '',
    supportCategories: [] as string[],
    termsAgreed: false
  });
  const [submitted, setSubmitted] = useState(false);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };
  const handleCategoryChange = (category: string) => {
    setFormData(prev => ({
      ...prev,
      supportCategories: prev.supportCategories.includes(category) ? prev.supportCategories.filter(c => c !== category) : [...prev.supportCategories, category]
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  const inputClass = `w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.inputOnCard}`;
  const labelClass = `block text-sm font-medium mb-1 ${dk.textSecondary}`;
  const sectionHeadingClass = `text-xl font-semibold mb-4 flex items-center ${dk.textPrimary}`;
  // Filters
  const filterSections: FilterSection[] = [{
    id: 'sort',
    title: 'Sort By',
    type: 'radio',
    options: [{
      value: 'relevance',
      label: 'Relevance'
    }, {
      value: 'rating-desc',
      label: 'Highest Rated'
    }],
    value: 'relevance',
    onChange: () => {}
  }, {
    id: 'availability',
    title: 'Availability',
    type: 'checkbox',
    options: [{
      value: 'open',
      label: 'Available Now'
    }, {
      value: 'waitlist',
      label: 'Waitlist'
    }],
    value: [],
    onChange: () => {}
  }, {
    id: 'serviceType',
    title: 'Service Type',
    type: 'checkbox',
    options: categories.map(c => ({
      value: c,
      label: c
    })),
    value: [],
    onChange: () => {}
  }];
  // Opportunities
  const opportunities: OpportunityItem[] = [{
    id: '1',
    title: 'Virtual Assistant for Romance Author',
    poster: 'Sarah Jenkins',
    deadline: '2023-11-30',
    type: 'Hourly',
    description: 'Seeking a VA to help with newsletter management, ARC team coordination, and social media scheduling.'
  }, {
    id: '2',
    title: 'Social Media Manager for Launch',
    poster: 'David Williams',
    deadline: '2023-12-15',
    type: 'Project Based',
    description: 'Need a social media expert to manage a 2-month campaign for a non-fiction book launch.'
  }];
  const formContent = <form onSubmit={handleSubmit} className="space-y-6" data-id="element-13917" data-mp="1zbp8flds120w">
      {/* Contact Information */}
      <div data-id="element-13918" data-mp="3poes6uo2wm71">
        <h2 className={sectionHeadingClass} data-id="element-13919" data-mp="3pooea760697g">
          <UserIcon className={`h-5 w-5 mr-2 ${rt.textPrimary}`} data-id="element-13920" data-mp="3toyk9jtsozmb" />
          Contact Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-13921" data-mp="34y10og3gs7s6">
          <div data-id="element-13922" data-mp="34w84juqh95yf">
            <label htmlFor="name" className={labelClass} data-id="element-13923" data-mp="2zz7yvkxji57u">
              Your Name *
            </label>
            <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} className={inputClass} required data-id="element-13924" data-mp="2v04qbqicpgv4" />
          </div>
          <div data-id="element-13925" data-mp="2m0zx8zpk6fqc">
            <label htmlFor="email" className={labelClass} data-id="element-13926" data-mp="2m1je0d3h2y2s">
              Email Address *
            </label>
            <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} className={inputClass} required data-id="element-13927" data-mp="2h6vl821frhie" />
          </div>
        </div>
      </div>

      {/* Service Requirements */}
      <div data-id="element-13928" data-mp="1y9cc6yez9yvz">
        <h2 className={sectionHeadingClass} data-id="element-13929" data-mp="1y9m4isnhwi4u">
          <BriefcaseIcon className={`h-5 w-5 mr-2 ${rt.textPrimary}`} data-id="element-13930" data-mp="1ybyr5j477go8" />
          Service Requirements
        </h2>
        <div className="space-y-4" data-id="element-13931" data-mp="htexlqwhu0ph">
          <div data-id="element-13932" data-mp="hsdkzhztx65n">
            <label htmlFor="serviceType" className={labelClass} data-id="element-13933" data-mp="hs3wjyufn722">
              Type of Service Needed *
            </label>
            <select id="serviceType" name="serviceType" value={formData.serviceType} onChange={handleInputChange} className={inputClass} required data-id="element-13934" data-mp="mqf2vzliqzfj">
              <option value="" data-id="element-13935" data-mp="3v7rxpzhy4bvq">Select service type</option>
              <option value="marketing" data-id="element-13936" data-mp="3v68go6ogtwsw">Marketing Support</option>
              <option value="administrative" data-id="element-13937" data-mp="418j098zo1s5">Administrative Support</option>
              <option value="research" data-id="element-13938" data-mp="8t096io8g5hr">Research Support</option>
              <option value="social-media" data-id="element-13939" data-mp="8ujni3qbw6gw">Social Media Management</option>
              <option value="author-assistant" data-id="element-13940" data-mp="37igxqk8f6lin">Author Assistant</option>
            </select>
          </div>
          <div data-id="element-13941" data-mp="3cfquym21w1h1">
            <label className={labelClass} data-id="element-13942" data-mp="34h4bprkfan4n">Support Categories</label>
            <div className="flex flex-wrap gap-2 mt-1" data-id="element-13943" data-mp="34fkr5orqhpx2">
              {categories.map(category => <button type="button" key={category} onClick={() => handleCategoryChange(category)} className={`px-3 py-1 rounded-full text-sm transition-all border ${formData.supportCategories.includes(category) ? `${rt.bgMedium} ${rt.textPrimary} ${rt.borderActive}` : `${dk.badgeNeutral} ${dk.border}`}`} data-id="element-13944" data-mp="30faejt9fs198">
                  {category}
                </button>)}
            </div>
          </div>
        </div>
      </div>

      {/* Project Details */}
      <div data-id="element-13945" data-mp="2qh3auc45rfp6">
        <h2 className={sectionHeadingClass} data-id="element-13946" data-mp="2qgbdavr037w9">
          <TagIcon className={`h-5 w-5 mr-2 ${rt.textPrimary}`} data-id="element-13947" data-mp="2qjpiscuynx7x" />
          Project Details
        </h2>
        <div data-id="element-13948" data-mp="as1nsmzicerr">
          <label htmlFor="projectDescription" className={labelClass} data-id="element-13949" data-mp="5rdwe07d9vx0">
            Project Description *
          </label>
          <textarea id="projectDescription" name="projectDescription" value={formData.projectDescription} onChange={handleInputChange} rows={4} className={inputClass} required data-id="element-13950" data-mp="1noh8orjz81jf" />
        </div>
      </div>

      {/* Terms and Submit */}
      <div className={`pt-4 border-t ${dk.border}`} data-id="element-13951" data-mp="1jnoz1x76zumm">
        <div className="flex items-start mb-6" data-id="element-13952" data-mp="27c7wtism8p0g">
          <div className="flex items-center h-5" data-id="element-13953" data-mp="27djcogzc5n7x">
            <input id="termsAgreed" name="termsAgreed" type="checkbox" checked={formData.termsAgreed} onChange={handleCheckboxChange} className={`h-4 w-4 border-gray-300 rounded cursor-pointer ${rt.checkbox}`} required data-id="element-13954" data-mp="2beuu3z823251" />
          </div>
          <div className="ml-3 text-sm" data-id="element-13955" data-mp="2qa0xsfjoiov7">
            <label htmlFor="termsAgreed" className={dk.textSecondary} data-id="element-13956" data-mp="2v2299z90wbk6">
              I agree to the{' '}
              <a href="#" className={`font-medium hover:underline ${rt.accentLink}`} data-id="element-13957" data-mp="uzt18h5nej3v">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="#" className={`font-medium hover:underline ${rt.accentLink}`} data-id="element-13958" data-mp="l9hukmukxvta">
                Privacy Policy
              </a>
            </label>
          </div>
        </div>
        <button type="submit" className={`w-full md:w-auto py-2 px-6 rounded-md font-medium text-white ${rt.primaryButton}`} data-id="element-13959" data-mp="1dyf0dqhlu055">
          Submit Request
        </button>
      </div>
    </form>;
  return <ConnectionPageLayout title="Connect with Support" subtitle="Submit your requirements to find the perfect support professional for your needs" icon={<HeartHandshakeIcon className="h-8 w-8" data-id="element-13961" data-mp="3osjiajs3jbuy" />} vendorType="support" filterSections={filterSections} opportunities={opportunities} opportunitiesLink="/services/support/opportunities" formContent={formContent} formCTALabel="Submit a Support Request" formCTADescription="Need help managing your author business? Submit your requirements and we'll match you." data-id="element-13960" data-mp="142lrch33gos4" />;
};
export default ConnectWithSupport;